/**
 * Parse a date string from the backend as UTC.
 *
 * The backend returns naive UTC datetimes without a timezone suffix (e.g. "2026-05-06T12:34:56").
 * Browsers interpret such strings as local time, causing incorrect display.
 * This function normalizes by appending 'Z' when no timezone info is present.
 */
export function parseUTCDate(dateString: string): Date {
  const hasTimezoneSuffix = /(?:Z|[+-]\d{2}:?\d{2})$/.test(dateString);
  const normalized = hasTimezoneSuffix ? dateString : dateString + 'Z';
  return new Date(normalized);
}

/**
 * Format a date string as a human-friendly relative time.
 *
 * Examples: "just now", "5m ago", "3h ago", "2d ago", "12 Mar"
 *
 * The fallback date is UK-style (day before month), like the other formatters
 * here: the users are European.
 */
export function formatRelativeTime(dateString: string): string {
  const date = parseUTCDate(dateString);

  // Validate the date is valid
  if (isNaN(date.getTime())) {
    return 'Unknown time';
  }

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-GB', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Format a date string as an absolute timestamp (e.g. "6 May, 04:52").
 * Used in activity feeds and event logs where exact time matters.
 */
export function formatAbsoluteTime(dateString: string): string {
  const date = parseUTCDate(dateString);

  if (isNaN(date.getTime())) {
    return 'Unknown time';
  }

  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a date string for comments — relative when recent, absolute otherwise.
 *
 * Examples: "3m ago", "14:32", "5 May, 14:32"
 */
export function formatCommentTime(dateString: string): string {
  const date = parseUTCDate(dateString);

  if (isNaN(date.getTime())) {
    return 'Unknown time';
  }

  const now = new Date();
  const diffMins = Math.floor((now.getTime() - date.getTime()) / 60000);

  if (diffMins < 60) return `${Math.max(diffMins, 1)}m ago`;

  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  const timeStr = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return timeStr;

  const dateStr = date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
  return `${dateStr}, ${timeStr}`;
}
